/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { XdaStringsService } from '../../services/xda-strings.service';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import {
  WorkflowStatusService,
  SqdStatus,
  SqdStatusState,
} from '../../services/workflow-status.service';
import {
  AutomatedJobHistory,
  AutomatedWorkflow,
  AutomatedWorkflowService,
} from '../../services/automated-workflow.service';
import { BasicAlertComponent } from '../alerts/basic-alert.component';
import { ActivityIndicatorModalComponent } from '../modals/activity-indicator-modal.component';
import { WorkflowHelperService } from '../../services/workflow-helper.service';

@Component({
  selector: 'xrx-job-history-modal',
  templateUrl: './xrx-job-history-modal.component.html',
  styleUrls: ['./xrx-job-history-modal.component.less'],
  standalone: false,
})
export class XrxJobHistoryModalComponent implements OnInit {
  @Input() workflow: AutomatedWorkflow;
  @Output() refreshJobs =
    new EventEmitter<AutomatedJobHistory[]>();

  statusStates: typeof SqdStatusState = SqdStatusState;
  jobs: AutomatedJobHistory[] = [];
  jobsReturned = false;
  showActivity = false;
  selectAll = false;
  isPartial = false;
  selectAllLabel = '';
  anySelected = false;
  displayPagination = true;
  page = 1;
  initialPageSize = 10;
  pageSize = 10;
  local: string = window.navigator.language || 'en-US';

  private xdaTranslatePipe: XdaTranslate;
  constructor(
    private automatedWorkflowService: AutomatedWorkflowService,
    private modalService: NgbModal,
    public activeModal: NgbActiveModal,
    private xdaStringsService: XdaStringsService,
    public workflowStatusService: WorkflowStatusService,
    public workflowHelperService: WorkflowHelperService,
  ) {
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
  }

  ngOnInit(): void {
    this.getJobHistory();
  }

  getJobHistory(): void {
    let occurrenceGroups: string[] = [],
      first: AutomatedJobHistory;

    // reset data
    this.jobs = [];
    this.jobsReturned = false;

    // pull in the job history
    this.showActivity = true;
    this.automatedWorkflowService
      .getJobHistory(this.workflow.id || '')
      .subscribe(
        (resp) => {
          this.showActivity = false;
          this.jobsReturned = true;
          this.jobs = resp;
          if (this.jobs && this.jobs.length > 0) {
            // reset select all checkbox
            this.selectAll = false;
            this.isPartial = false;

            // determine if we should display pagination
            if (this.jobs.length <= this.pageSize) {
              this.displayPagination = false;
            } else {
              this.displayPagination = true;
            }
            // select all label
            this.selectAllLabel = this.xdaTranslatePipe.transform(
              'SDE_SELECTED_FMTSTR_FMTSTR',
              ['0', String(this.jobs.length)],
            );

            // loop through the job history and add formatted entries needed for the UI
            this.jobs.forEach((j) => {
              j.submitted = this.formatSubmitTime(j.initiatedAt);
              j.occurrenceString = `${this.xdaTranslatePipe.transform(j.jobOccurrence.occurrence)}: ${this.formatOccurrenceDate(j.initiatedAt, j.jobOccurrence.occurrence)}`;
              j.pageCountString = `(${j.pageCount} ${this.xdaTranslatePipe.transform('SDE_PAGES').toLocaleLowerCase()})`;

              // collect occurrences
              occurrenceGroups.push(j.jobOccurrence.date);
            });

            // remove duplicates from occurrenceGroups
            occurrenceGroups = Array.from(new Set(occurrenceGroups));

            // add hasHeader true to first job in each occurrence
            occurrenceGroups.forEach((g) => {
              const first = this.jobs.find((j) => j.jobOccurrence.date == g);
              if (first) {
                first.hasHeader = true;
              }
            });

            // notify subscribers of job counts
            this.refreshJobs.emit(resp);
          } else if (this.jobs && this.jobs.length <= 0) {
            // notify subscribers of job 0 job count
            this.refreshJobs.emit(resp);
          }
        },
        () => {
          this.showActivity = false;
          const modalRef = this.modalService.open(BasicAlertComponent);
          modalRef.componentInstance.title = 'SDE_ERROR_GETTING_AUTOMATED';
          modalRef.componentInstance.body = 'SDE_PLEASE_TRY_AGAIN1';
          this.jobsReturned = true;
        },
      );
  }

  // function to format submitted time
  formatSubmitTime(date: any): string {
    let formattedDate = '';
    date = typeof date === 'string' ? new Date(date) : date;
    formattedDate = date.toLocaleString(this.local, {
      hour: '2-digit',
      minute: 'numeric',
      second: 'numeric',
    });
    return formattedDate;
  }
  // function to format occurrence date
  formatOccurrenceDate(date: any, occurrence: string): string {
    let formattedDate = '';
    date = typeof date === 'string' ? new Date(date) : date;
    if (occurrence == 'SDE_HOURLY') {
      formattedDate = date.toLocaleString(this.local, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: 'numeric',
        second: 'numeric',
      });
    } else {
      formattedDate = date.toLocaleString(this.local, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }

    return formattedDate;
  }

  // toggle all visible jobs
  toggleAll(value: boolean): void {
    this.selectAll = value;
    this.isPartial = false;

    // check or uncheck all jobs
    this.jobs.forEach((j) => {
      j.checked = value;
    });
    this.anySelected = this.anyJobsSelected();
    this.setSelectAllLabel();
  }

  // when a job is checked make sure UI is updated
  onChecked(value: boolean, job: any): void {
    job.checked = value;
    this.anySelected = this.anyJobsSelected();
    this.setSelectAllLabel();
  }

  // set the checkbox value when a job row is clicked
  rowClicked(job: AutomatedJobHistory): void {
    job.checked = !job.checked;
    this.anySelected = this.anyJobsSelected();
    this.setSelectAllLabel();
  }

  setSelectAllLabel(): void {
    this.selectAllLabel = this.xdaTranslatePipe.transform(
      'SDE_SELECTED_FMTSTR_FMTSTR',
      [
        this.jobs.filter((u) => u.checked == true).length.toString(),
        String(this.jobs.length),
      ],
    );
  }

  // check to see any of the visible jobs are selected or not
  anyJobsSelected(): boolean {
    const numberOfJobsSelected = this.jobs.filter((j) => j.checked == true);

    let isPartial = false,
      jobsSelected = false;

    // set the value of the selectAll checkbox
    this.selectAll =
      numberOfJobsSelected.length == this.jobs.length ? true : false;

    isPartial =
      numberOfJobsSelected.length < this.jobs.length &&
      numberOfJobsSelected.length > 0
        ? true
        : false;

    this.isPartial = isPartial;
    jobsSelected = numberOfJobsSelected.length > 0;

    return jobsSelected;
  }

  removeSelectedJobs(): void {
    const checked: string[] = [],
      modalActivityRef = this.modalService.open(
        ActivityIndicatorModalComponent,
        {
          windowClass: 'xrx-modal transparent',
          centered: true,
          backdrop: 'static',
        },
      );

    this.jobs.forEach((j) => {
      if (j.checked == true) {
        checked.push(j.id);
      }
    });

    this.automatedWorkflowService
      .deleteJobHistory(this.workflow.id || '', checked)
      .subscribe(
        (resp) => {
          this.getJobHistory();
          this.selectAll = false;
          this.page = 1;
          modalActivityRef.close();
        },
        (err) => {
          this.showActivity = false;
          modalActivityRef.close();
          const modalRef = this.modalService.open(BasicAlertComponent);
          modalRef.componentInstance.title = 'SDE_ERROR_REMOVING_AUTOMATED';
          modalRef.componentInstance.body = 'SDE_PLEASE_TRY_AGAIN1';
          this.jobsReturned = true;
          this.selectAll = false;
        },
      );
  }

  report(job: AutomatedJobHistory): void {
    if (this.isErrorStateArray(job.status)) {
      this.workflowStatusService.errorReportWorkflow(job, true);
    }
  }

  isErrorStateArray(status: SqdStatus[]): boolean {
    try {
      return status.some((s) => s.state == this.statusStates.Errored);
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  getErrorMessage(status: SqdStatus[]): string {
    const errorStatus = status.find(
      (s) => s.state == this.statusStates.Errored,
    );
    const isOutputStatus =
      errorStatus?.serviceId == '00000000-0000-0000-0000-000000000000';

    let errorMessage = isOutputStatus
        ? errorStatus?.statusRaw
        : errorStatus?.status,
      serviceName = isOutputStatus
        ? errorStatus?.status
        : errorStatus?.serviceName;

    if (isOutputStatus && errorStatus?.status == 'SDE_TIMEOUT') {
      const timeOutServiceName = errorStatus?.serviceName
        ? this.xdaStringsService.strings[errorStatus.serviceName]
        : errorStatus?.serviceName;
      return `${timeOutServiceName} ${this.xdaStringsService.strings['SDE_TIMEOUT']}`;
    }

    // translate page range errors
    if (errorMessage?.includes('SDE_INVALID_PAGE_RANGE')) {
      const splitStatus = errorMessage.split(':');
      const translatedStatus = this.xdaStringsService.strings[splitStatus[0]];
      errorMessage = translatedStatus + ': ' + splitStatus[1];
    }
    if (serviceName?.includes('SDE_INVALID_PAGE_RANGE')) {
      const splitStatus = serviceName.split(':');
      const translatedStatus = this.xdaStringsService.strings[splitStatus[0]];
      serviceName = translatedStatus + ': ' + splitStatus[1];
    }

    const translatedServiceName = serviceName
      ? this.xdaStringsService.strings[serviceName]
      : serviceName;
    const translatedErrorMessage = errorMessage
      ? this.xdaStringsService.strings[errorMessage]
      : errorMessage;
    if (
      translatedErrorMessage &&
      translatedServiceName == translatedErrorMessage
    ) {
      return translatedErrorMessage;
    }

    return `${translatedServiceName} - ${translatedErrorMessage}`;
  }
}
